export const OTP_CODE_LENGTH = 6

// The emailed code is six characters displayed as `XXX-XXX`. The separator is
// presentational only — it is re-derived from the code characters on every edit.
const SEPARATOR_POSITION = 3

// Keeps only the characters that make up the code, discarding whitespace,
// hyphens of any flavour (ASCII, en dash, em dash...) and anything else that
// comes along with a pasted code.
//
// The filter runs before toUpperCase() on purpose: uppercasing can turn one
// non-ASCII character into several ASCII ones ('ß' becomes 'SS', 'ﬁ' becomes
// 'FI'), which would otherwise smuggle code characters in from input that
// should have been discarded outright.
const toCodeCharacters = (value: string) =>
  value
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, OTP_CODE_LENGTH)

const withSeparator = (codeCharacters: string) =>
  codeCharacters.length > SEPARATOR_POSITION
    ? `${codeCharacters.slice(0, SEPARATOR_POSITION)}-${codeCharacters.slice(
        SEPARATOR_POSITION,
      )}`
    : codeCharacters

/**
 * Normalises whatever the input element currently holds into `XXX-XXX` and
 * works out where the cursor belongs afterwards.
 *
 * The cursor is tracked by counting code characters rather than raw offsets, so
 * re-inserting the separator does not drag it to the end of the field.
 */
export const formatOtpInput = (
  rawValue: string,
  rawCursorPosition: number,
): { value: string; cursorPosition: number } => {
  const codeCharacters = toCodeCharacters(rawValue)
  const codeCharactersBeforeCursor = Math.min(
    toCodeCharacters(rawValue.slice(0, rawCursorPosition)).length,
    codeCharacters.length,
  )

  return {
    value: withSeparator(codeCharacters),
    cursorPosition:
      codeCharactersBeforeCursor > SEPARATOR_POSITION
        ? codeCharactersBeforeCursor + 1
        : codeCharactersBeforeCursor,
  }
}

export const isCompleteOtp = (value: string) =>
  toCodeCharacters(value).length === OTP_CODE_LENGTH
